import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import { IconButton, Box, useMediaQuery } from "@mui/material";
import { FaRobot, FaWhatsapp } from "react-icons/fa";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';

import Header from "./components/Header";
import Footer from "./components/Footer";
import EnhancedChatbox from "./components/EnhancedChatbox";
import LoadingScreen from "./components/LoadingScreen";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import NewsMedia from "./pages/NewsMedia";

// Service Detail Pages
import WebDevelopment from "./pages/services/WebDevelopment";
import ArtificialIntelligence from "./pages/services/ArtificialIntelligence";
import DataAnalytics from "./pages/services/DataAnalytics";
import Cybersecurity from "./pages/services/Cybersecurity";
import GraphicDesign from "./pages/services/GraphicDesign";
import AppDevelopment from "./pages/services/AppDevelopment";
import IoTEmbedded from "./pages/services/IoTEmbedded";
import CloudComputing from "./pages/services/CloudComputing";
import Blockchain from "./pages/services/Blockchain";
import DigitalMarketing from "./pages/services/DigitalMarketing";

import "./App.css";

// ✅ Custom theme with Lato font
import ScrollToTop from "./components/ScrollToTop";

// ... existing code ...

const theme = responsiveFontSizes(createTheme({
  palette: {
    primary: { main: "#034522" },
    secondary: { main: "#1A9E7F" },
  },
  typography: {
    fontFamily: "Lato, sans-serif",
    h1: { fontFamily: "'Lato', sans-serif" },
    h2: { fontFamily: "'Lato', sans-serif" },
    h3: { fontFamily: "'Lato', sans-serif" },
    h4: { fontFamily: "'Lato', sans-serif" },
    h5: { fontFamily: "'Lato', sans-serif" },
    h6: { fontFamily: "'Lato', sans-serif" },
    button: { fontFamily: "'Lato', sans-serif", fontWeight: 700 },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
}));

// Page transition wrapper component
const PageTransition = ({ children }) => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  const [showChatbox, setShowChatbox] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    // Hide loading screen after initial load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const toggleChatbox = () => setShowChatbox((prev) => !prev);

  // ✅ WhatsApp Link (replace with your number)
  const openWhatsApp = () => {
    window.open("https://wa.me/918300619277", "_blank");
  };

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <LoadingScreen />
        
        {!isLoading && (
          <>
            <ScrollToTop /> {/* ✅ Ensures page starts at top on navigation */}
            {/* ✅ Fixed Header */}
            <Box sx={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1100 }}>
              <Header />
            </Box>

            {/* ✅ Main Content (Centered Sections) */}
            <Box>
              <Routes>
                {/* ✅ Main Routes */}
                <Route path="/" element={
                  <PageTransition>
                    <Home />
                  </PageTransition>
                } />
                <Route path="/about" element={
                  <PageTransition>
                    <About />
                  </PageTransition>
                } />
                <Route path="/services" element={
                  <PageTransition>
                    <Services />
                  </PageTransition>
                } />
                <Route path="/contact" element={
                  <PageTransition>
                    <Contact />
                  </PageTransition>
                } />
                <Route path="/careers" element={
                  <PageTransition>
                    <Careers />
                  </PageTransition>
                } />
                <Route path="/news" element={
                  <PageTransition>
                    <NewsMedia />
                  </PageTransition>
                } />

                {/* Service Detail Pages */}
                <Route path="/services/web-development" element={
                  <PageTransition>
                    <WebDevelopment />
                  </PageTransition>
                } />
                <Route path="/services/artificial-intelligence" element={
                  <PageTransition>
                    <ArtificialIntelligence />
                  </PageTransition>
                } />
                <Route path="/services/data-analytics" element={
                  <PageTransition>
                    <DataAnalytics />
                  </PageTransition>
                } />
                <Route path="/services/cybersecurity" element={
                  <PageTransition>
                    <Cybersecurity />
                  </PageTransition>
                } />
                <Route path="/services/graphic-design" element={
                  <PageTransition>
                    <GraphicDesign />
                  </PageTransition>
                } />
                <Route path="/services/app-development" element={
                  <PageTransition>
                    <AppDevelopment />
                  </PageTransition>
                } />
                <Route path="/services/iot-embedded" element={
                  <PageTransition>
                    <IoTEmbedded />
                  </PageTransition>
                } />
                <Route path="/services/cloud-computing" element={
                  <PageTransition>
                    <CloudComputing />
                  </PageTransition>
                } />
                <Route path="/services/blockchain" element={
                  <PageTransition>
                    <Blockchain />
                  </PageTransition>
                } />
                <Route path="/services/digital-marketing" element={
                  <PageTransition>
                    <DigitalMarketing />
                  </PageTransition>
                } />
                <Route path="/privacy-policy" element={
                  <PageTransition>
                    <PrivacyPolicy />
                  </PageTransition>
                } />
                <Route path="/terms-of-service" element={
                  <PageTransition>
                    <TermsOfService />
                  </PageTransition>
                } />
              </Routes>

              <Footer />
            </Box>
          </>
        )}

        {/* ✅ WhatsApp Floating Button - Bottom Right */}
        <IconButton
          onClick={openWhatsApp}
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 30 },
            right: { xs: 20, md: 30 },
            backgroundColor: "#25D366",
            color: "white",
            "&:hover": { backgroundColor: "#128C7E" },
            zIndex: 1500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            width: { xs: 50, md: 60 },
            height: { xs: 50, md: 60 }
          }}
        >
          <FaWhatsapp size={isMobile ? 24 : 32} />
        </IconButton>

        {/* ✅ Floating Robot Icon - Bottom Right (Above WhatsApp) */}
        <IconButton
          onClick={toggleChatbox}
          sx={{
            position: "fixed",
            bottom: { xs: 80, md: 100 }, // Stacked above WhatsApp
            right: { xs: 20, md: 30 },
            backgroundColor: "secondary.main",
            color: "white",
            "&:hover": { backgroundColor: "primary.main", transform: "scale(1.1)" },
            zIndex: 1500,
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            width: { xs: 50, md: 60 },
            height: { xs: 50, md: 60 },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}
        >
          <FaRobot size={isMobile ? 24 : 32} />
        </IconButton>

        {/* ✅ Background Blur Effect */}
        {showChatbox && (
          <Box
            className="blur-background"
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 1550,
            }}
            onClick={toggleChatbox}
          />
        )}

        {/* ✅ Full-Screen Chatbot - Fixed for Small Devices */}
        {showChatbox && (
          <Box
            className="fullscreen-chatbot"
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              animation: "slideInUp 0.3s ease-out",
              bgcolor: "white",
              overflow: "hidden",
              transform: "translateZ(0)",
              willChange: "transform"
            }}
          >
            <EnhancedChatbox onClose={toggleChatbox} />
          </Box>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
